/**
 * Locates the custom-variable row for the chip id in task vs workflow-description context.
 * @author Abhishek Dhillon
 * @param {Object} params
 * @param {string|undefined} params.smartChipId
 * @param {Array|undefined} params.taskCustomVariables
 * @param {string|number|undefined} params.workflowContextId - `workflowState?.workflow?.Id` when editing workflow description
 * @returns {Object|undefined}
 */
export const findExistingTaskCustomVariable = ({ smartChipId, taskCustomVariables, workflowContextId }) => {
  if (!smartChipId) return undefined;
  if (workflowContextId) {
    return taskCustomVariables?.find((variable) => variable.workflowDescriptionVariableId === smartChipId);
  }
  return taskCustomVariables?.find((variable) => variable.taskVariableId === smartChipId);
};

/**
 * Prefers prop `variableDetails`, then project/workflow metadata stored on the existing chip row.
 * @author Abhishek Dhillon
 * @param {Object|undefined} variableDetails
 * @param {Object|undefined} existingVariable
 * @returns {Object|undefined}
 */
export const resolveTaskVariableDetails = (variableDetails, existingVariable, workflowCustomVariables) => {
  if(variableDetails) return variableDetails;
  let existingVarObj = existingVariable?.projectVariableDetails || existingVariable?.workflowVariableDetails;
  if(typeof existingVarObj === 'object') {
    return existingVarObj;
  }
  let findId = existingVariable?.projectVariableId || existingVariable?.workflowVariableId;
  let findDetails = workflowCustomVariables?.find((variable) => variable?.id === findId);
  return findDetails;
}; 

/**
 * Builds shared custom-variable payloads for task vs workflow description contexts.
 * @author Abhishek Dhillon
 * @param {Object} options
 * @param {Object} options.variableDetails - Workflow or project variable metadata from the chip
 * @param {string|number} options.userId - Current user id
 * @param {*} options.variableValue - Value to store (string, date, dropdown object, etc.)
 * @param {string} options.generatedId - Unique id for the new chip (e.g. from `generateUniqueId`)
 * @returns {{ commonVariable: Object, newTaskCustomVariable: Object, newWorkflowCustomVariable: Object }}
 */
export const buildTaskCustomVariablePayloads = ({ variableDetails, userId, variableValue, generatedId }) => {
  const commonVariable = {
    variableName: variableDetails?.name,
    userId,
    variableValue,
    ...(variableDetails?.isProjectVariable
      ? { projectVariableId: variableDetails?.id, projectVariableDetails: variableDetails?.id }
      : { workflowVariableId: variableDetails?.id, workflowVariableDetails: variableDetails?.id }),
  };

  return {
    commonVariable,
    newTaskCustomVariable: {
      ...commonVariable,
      taskVariableId: generatedId,
    },
    newWorkflowCustomVariable: {
      ...commonVariable,
      workflowDescriptionVariableId: generatedId,
    },
  };
};
